/* eslint no-console: "off" */
/**
 * Service worker registration for the Owner's Kit PWA.
 *
 * Uses `workbox-window` (the small client-side counterpart to Workbox in the
 * SW) so we get:
 *   - typed lifecycle events (`waiting`, `controlling`, `installed`)
 *   - a clean way to message `SKIP_WAITING` to the worker once the user
 *     accepts an update
 *
 * The SW file (`/service-worker.js`) is generated at build time by
 * `scripts/build-sw.js`. Only registered in production — CRA's dev server
 * has no SW and HMR would fight one anyway.
 *
 * Usage:
 *   import * as serviceWorkerRegistration from './serviceWorkerRegistration'
 *   serviceWorkerRegistration.register({
 *     onUpdate: (workbox) => { ...show "refresh" toast... },
 *     onSuccess: () => { ...optional first-install hook... },
 *   })
 */

import { Workbox } from 'workbox-window'

const isLocalhost = Boolean(
  typeof window !== 'undefined' &&
    (window.location.hostname === 'localhost' ||
      window.location.hostname === '[::1]' ||
      window.location.hostname.match(
        /^127(?:\.(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)){3}$/
      ))
)

let wb = null

export function getWorkbox() {
  return wb
}

export function register(config = {}) {
  if (
    process.env.NODE_ENV !== 'production' ||
    typeof window === 'undefined' ||
    !('serviceWorker' in navigator)
  ) {
    return
  }

  // Respect CRA's PUBLIC_URL — the project ships with `homepage: "."` so the
  // app may be hosted under a sub-path. Ensure the SW is registered with a
  // matching scope.
  const publicUrl = new URL(
    process.env.PUBLIC_URL || '/',
    window.location.href
  )
  if (publicUrl.origin !== window.location.origin) {
    // CDN-hosted assets — SW can't span origins. Bail.
    return
  }

  window.addEventListener('load', () => {
    const swUrl = `${publicUrl.pathname.replace(/\/$/, '')}/service-worker.js`

    if (isLocalhost) {
      checkValidServiceWorker(swUrl, config)
      navigator.serviceWorker.ready.then(() => {
        console.log(
          '[PWA] This web app is being served cache-first by a service worker.'
        )
      })
    } else {
      registerValidSW(swUrl, config)
    }
  })
}

function registerValidSW(swUrl, config) {
  wb = new Workbox(swUrl)

  // A new SW has installed and is waiting to activate. We don't auto-activate
  // because that would reload tabs unexpectedly mid-interaction.
  wb.addEventListener('waiting', () => {
    console.log('[PWA] New content is available; waiting to activate.')
    if (typeof config.onUpdate === 'function') {
      config.onUpdate(wb)
    }
  })

  // Once the new SW takes control (after the user accepts the update), reload
  // the page so the freshly cached assets are used.
  wb.addEventListener('controlling', () => {
    if (!window.__pwaReloading) {
      window.__pwaReloading = true
      window.location.reload()
    }
  })

  wb.addEventListener('activated', event => {
    if (!event.isUpdate) {
      console.log('[PWA] Content is cached for offline use.')
      if (typeof config.onSuccess === 'function') {
        config.onSuccess(wb)
      }
    }
  })

  wb.register().catch(err => {
    console.error('[PWA] Service worker registration failed:', err)
  })
}

// On localhost we sometimes have a stale SW from a previous build. Verify
// the SW URL still resolves to a real JS file before registering — if it
// 404s or returns HTML, unregister and reload.
function checkValidServiceWorker(swUrl, config) {
  fetch(swUrl, { headers: { 'Service-Worker': 'script' } })
    .then(response => {
      const contentType = response.headers.get('content-type') || ''
      if (
        response.status === 404 ||
        contentType.indexOf('javascript') === -1
      ) {
        navigator.serviceWorker.ready.then(registration => {
          registration.unregister().then(() => {
            window.location.reload()
          })
        })
      } else {
        registerValidSW(swUrl, config)
      }
    })
    .catch(() => {
      console.log(
        '[PWA] No internet connection found. App is running in offline mode.'
      )
    })
}

// Tell the waiting SW to activate. Called by the UpdatePrompt component when
// the user clicks "Refresh now". wb.messageSkipWaiting() triggers
// SKIP_WAITING which fires `controlling` above and reloads the page.
export function applyUpdate() {
  if (wb) {
    wb.messageSkipWaiting()
  }
}

export function unregister() {
  if ('serviceWorker' in navigator) {
    navigator.serviceWorker.ready
      .then(registration => registration.unregister())
      .catch(err => console.error(err.message))
  }
}
